import { COUNTRIES } from "../../constants/common/common";
import { removeEmptyNullAndUndefinedFromObject, withLatestLocation } from "../../utils/common";

/**
 * Represents the API endpoint for the home page data in the website.
 * @constant
 * @type {string}
 */
export const CMS_API_VERSION = `/chocolate/v1/`;
/**
 * TTL (ms) for PDP CMS batch in `withServerCache` on each Next.js Node process.
 */
export const CURRENCY_LIST_SERVER_CACHE_TTL_MS = 1800000; // 30 minutes

/**
 *
 * @param {*} productId
 * @param {*} pinCode
 * @param {*} currencyUomId
 * @param {*} catalogId
 * @param {*} countryGeoId
 * @returns
 */
export const GET_PRICE_AND_EARLIEST_SHIP_DETAILS_URL = (
  productId,
  pinCode,
  currencyUomId,
  catalogId,
  countryGeoId,
  partyId,
  from,
  lat,
  long,
  accuracyType,
  isSelectedAddressLocation = false,
) => {
  const location = withLatestLocation({ lat, long, accuracyType, isSelectedAddressLocation });
  return `/serviceability/v1/product/delivery-dates?productId=${productId}&pinCode=${pinCode}&currencyUomId=${currencyUomId}&catalogId=${catalogId}&countryGeoId=${countryGeoId}${partyId ? `&pId=${partyId}` : ""}${from ? `&from=${from}` : ""}${location.lat ? `&lat=${location.lat}` : ""}${location.long ? `&long=${location.long}` : ""}${location.accuracyType ? `&accuracyType=${location.accuracyType}` : ""}`;
};

/**
 *
 * @param {*} productId
 * @param {*} pinCode
 * @param {*} currencyUomId
 * @param {*} catalogId
 * @param {*} countryGeoId
 * @param {*} deliveryDate
 * @returns
 */
export const GET_EARLIEST_TIME_SLOT_URL = ({
  productId,
  pinCode,
  currencyUomId,
  catalogId,
  countryGeoId,
  deliveryDate,
  lat,
  long,
}) => {
  const query = new URLSearchParams(
    removeEmptyNullAndUndefinedFromObject({
      productId,
      pinCode,
      currencyUomId,
      catalogId,
      countryGeoId,
      date: deliveryDate,
      ...withLatestLocation({ lat, long }),
    }),
  ).toString();

  return `/serviceability/v1/earliest/slot?${query}`;
};

/**
 *
 * @param {*} productId
 * @param {*} pinCode
 * @returns
 */
export const GET_BLOCKED_DATES_URL = (productId, pinCode) => {
  return `/productapi/api/rest/v1.3/getBlockedDates?pinCode=${pinCode}&productId=${productId}`;
};

/**
 *
 * @param {*} productId
 * @param {*} pinCode
 * @param {*} deliveryDate
 * @param {*} countryGeoId
 * @returns
 */
export const GET_SHIPPING_AND_TIME_SLOT_URL = (productId, pinCode, deliveryDate, countryGeoId) => {
  return `/productapi/api/rest/v1.3/shippingAndTimeSlot?productId=${productId}&pinCode=${pinCode}&deliveryDate=${deliveryDate}&countryGeoId=${countryGeoId}`;
};

/**
 * Checkout serviceability shipping-details endpoint (gateway).
 *
 * @param {Object} params
 * @returns {string}
 */
export const GET_CHECKOUT_SHIPPING_DETAILS_URL = ({
  productId,
  pinCode,
  deliveryDate,
  countryGeoId = "IND",
  lat = 27,
  long = 78,
  accuracyType,
  isSelectedAddressLocation = false,
}) => {
  const query = new URLSearchParams(
    removeEmptyNullAndUndefinedFromObject({
      productId,
      pinCode,
      deliveryDate,
      countryGeoId,
      ...withLatestLocation({ lat, long, accuracyType, isSelectedAddressLocation }),
    }),
  ).toString();

  return `/serviceability/v1/shipping-details?${query}`;
};

/**
 *
 * @param {*} productId
 * @param {*} pinCode
 * @param {*} currencyUomId
 * @param {*} deliveryDate
 * @param {*} catalogId
 * @returns
 */
export const GET_DELIVERY_DATE_AND_GEO_PRICE = (
  productId,
  pinCode,
  currencyUomId,
  deliveryDate,
  catalogId,
  partyId,
) => {
  return `/productapi/api/rest/v1.3/getDeliveryDateAndGeoPrice?productId=${productId}&geoId=${pinCode}&currencyUomId=${currencyUomId}&deliveryDate=${deliveryDate}&catalogId=${catalogId}${partyId ? `&pId=${partyId}` : ""}`;
};

/** */
export const GET_SEARCH_SUGGESTIONS = (geoId, keyword, uuid, userId) => {
  return `/columbus/v1/suggestions?geoId=${geoId}&keyword=${keyword}&domainId=fnp.com&uuid=${uuid}&lang=en${userId ? `&userId=${userId}` : ""}`;
};

/** */
export const GET_AUTO_SUGGESTIONS = (geoId, keyword, pincode) => {
  return `/search/v1/autosuggest?geoId=${geoId}&keyword=${keyword}${pincode ? `&pincode=${pincode}` : ""}`;
};

/** */
export const GET_CURRENCY_LIST = () => {
  return "/control/getCurrencies";
};
/**
 * @returns
 */
export const GET_DYNAMIC_SEARCH_TEXT_PLACEHOLDER = () => {
  return `/chocolate/v1/global/static/web/appdynamicsearchtextsuggestions`;
};

/** Export FUS API Endpoint constant
 * @returns FUS endpoint
 */
export const FUS_API = () => {
  return "/control/fus";
};

/**
 * Export CLEAR CART API Endpoint constant
 * @returns CLEAR CART endpoint
 */
export const CLEAR_CART_API = () => {
  return "/control/clearCart";
};

/**
 * Constructs the URL for fetching add-on information based on the provided parameters.
 *
 * @param {Object} props - An object containing parameters to filter the add-on information.
 * @returns {string} Returns the constructed URL for the add-on info API endpoint,
 *                  including query string parameters for filtering.
 */
export const GET_ADD_ON_INFO = (props) => {
  const filteredParams = removeEmptyNullAndUndefinedFromObject(props);
  const flattenedParams = new URLSearchParams(filteredParams).toString();

  return `/control/getAddonsInfo-rj?${flattenedParams}`;
};

/***
 * Constructs the URL for fetching add-on information based on the provided parameters.
 *
 * @param {Object} props - An object containing parameters to filter the add-on information.
 * @returns {string} Returns the constructed URL for the add-on info API endpoint,
 *                  including query string parameters for filtering.
 */
export const GET_ADD_ON_INFO_NEW = (props) => {
  const filteredParams = removeEmptyNullAndUndefinedFromObject(props);
  const flattenedParams = new URLSearchParams(filteredParams).toString();

  return `/control/getAddonsInfo-rj?${flattenedParams}`;
};

/**
 * Constructs the URL for adding add-ons to the shopping cart.
 *
 * @returns {string} Returns the API endpoint URL for adding add-ons to the cart.
 */
export const ADD_ONS_TO_CART = () => {
  return `/control/addAddonsToCart-rj`;
};

/** Get the Mega Menu content for mobile.
 * @returns {string} Returns the API endpoint URL for Mega Menu.
 */
export const GET_MEGA_MENU = (catalogID) => {
  const countryID = catalogID !== COUNTRIES.INDIA ? `${catalogID}/` : "";
  return `/chocolate/v1/menus-alt/${countryID}default-menu-hm`;
};

/** Get the Mega Menu content for desktop.
 * @returns {string} Returns the API endpoint URL for Mega Menu.
 */
export const GET_MEGA_MENU_DESKTOP = (catalogID) => {
  const countryID = catalogID !== COUNTRIES.INDIA ? `${catalogID}/` : "";
  return `/chocolate/v1/menus/${countryID}default-menu-hm`;
};

/**
 * API endpoint URL to fetch the SUBSCRIBE_NEW_USER response.
 */
export const SUBSCRIBE_NEW_USER = () => {
  return `/control/subscribe-user`;
};

/**
 * This method fetches Timing for CALL and CHAT in my account.
 */
export const GET_CALL_CHAT_TIME = () => {
  return `/chocolate/v1/global/static/web/configurecallandchattime`;
};

/**
 * This method fetches International shipping method and time slot.
 */
export const GET_INTERNATIONAL_SHIPPING_AND_TIME_SLOT = (productId, city) => {
  return `/productapi/api/rest/v1.3/getShippingMethodsAndTimeSlotsForINTL?productId=${productId}&city=${city}`;
};

/**
 * This method fetches International delivery dates list.
 */
export const GET_INTERNATIONAL_DELIVERY_DATES = (productId, city, shipmentType, timeSlotId) => {
  const localParams = { productId, city, shipmentType, timeSlotId };
  const filteredParams = removeEmptyNullAndUndefinedFromObject(localParams);
  const flattenedParams = new URLSearchParams(filteredParams).toString();
  return `/productapi/api/rest/v1.3/getDeliveryDatesForINTL?${decodeURIComponent(flattenedParams)}`;
};

/**
 * API endpoint URL to fetch the Saved Address response.
 */
export const GET_SAVED_ADDRESSES = () => {
  return `/control/getSavedRecipientAddresses?viewSize=40&viewIndex=0`;
};

/**
 * This method fetches popular link desktop data for OOPS 404 page.
 */
export const GET_POPULAR_LINK404_DESKTOP = () => {
  return `/chocolate/v1/global/static/web/desktop/popular-link-404-desktop-new`;
};

/**
 * This method fetches popular link mobile data for OOPS 404 page.
 */
export const GET_POPULAR_LINK404_MOBILE = () => {
  return `/chocolate/v1/global/static/web/mobile/popular-link-404-mobile-new`;
};

/**
 * Returns the endpoint URL for Top app banner data. .
 */
export const GET_APP_BANNER_DATA_URL = () => {
  return "global/static/web/mobile/fnp_app_redirect";
};

/**
 * Returns the endpoint URL for Clevertap Publish. .
 */
export const GET_APP_CLEVERTAP_PUBLISH_URL = () => {
  return `/sls/v1/publish`;
};

/**
 * Returns the endpoint URL for check where same day category available .
 */
export const GET_IS_SAME_DAY_CATALOG = () => {
  return "global/static/web/issamedaycategoryincatalog";
};

/**
 * Returns the endpoint URL for Clevertap Publish. .
 */
export const GET_GEOJS_URL = () => {
  return "https://get.geojs.io/v1/ip/geo.json";
};
/**
 * Returns the endpoint URL for Clevertap disabled list.
 */
export const GET_CT_DISABLED_EVENT_LIST_URL = () => {
  return "/chocolate/v1/global/static/ct_events_disabled_list";
};
/**
 * Returns the endpoint URL for Google place type accuracy weightage.
 */
export const GET_ACCURACY_WEIGHTAGE_URL = () => {
  return "/serviceability/v1/accuracy-weightage";
};
/**
 * Returns the endpoint URL for currency data with flag and icon.
 */
export const GET_CURRENCY_FLAG_ICON = () => {
  return "/chocolate/v1/global/static/web/currency-data";
};
/**
 * Returns the endpoint URL for Lottie icons.
 */
export const GET_LOTTIE_ICONS = () => {
  return "/chocolate/v1/global/static/web/lottiefiles";
};

/**
 * Returns the endpoint URL for free delivery banner data.
 */
export const GET_FREE_DELIVERY_BANNER_URL = () => {
  return "global/static/web/freedelivery";
};

/**
 * Returns the boolean flag to show free delivery tag on PDP.
 */
export const GET_SHOW_FREE_DELIVERY_FOR_PDP_URL = () => {
  return "global/static/web/freedeliverytagforpdp";
};

/**
 * Returns the boolean flag to show free delivery tag on PDP.
 */
export const GET_REDIRECTION_OCCASION_NUDGE_DATA = () => {
  return "global/static/web/redirectionoccasionnudge";
};

/**
 * Returns the boolean flag to check if the partner is linked.
 */
export const GET_IS_PARTNER_LINKED = (partnerId) => {
  return `/control/getPartnerLoyaltyLink-rj?partnerId=${partnerId}`;
};

/**
 * Returns the boolean flag to check if the partner is linked.
 */
export const PARTNER_LOYALTY_LINK_UPDATE = () => {
  return "/control/updatePartnerLoyaltyLink-rj";
};

/**
 * Returns the boolean flag to check if the partner is linked.
 */
export const PARTNER_LOYALTY_CONTENT = () => {
  return "/control/getContentIdDetails?contentId=FNP_PARTNER_LOYALTY";
};
